import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Dashboard from "components/dashboard";
import { PageHeaderBar, PageSection } from "components/page";
import DataGrid from "components/data-grid";
import { defaultValues, RoomTemplateForm, validationSchema } from "./forms/new-form";
import { checkEvuService, createRoomTemplateService, updateRoomTemplateService } from "../../services/evu-services";
import { getDeploymentStreamSourcesService } from "../../services/deploymentServices";
import { HTTP_CODE_CREATED, HTTP_CODE_OK, HTTP_CODE_UPDATED } from "../../utils/constants";
import { useLoadingContext } from "../../components/loading-spinner-overlay";
import Alert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { getEnvironment } from "../../utils/utils";
import Button from "../../components/buttons/button";
import { FormattedMessage } from "react-intl";
import { withStyles } from "@material-ui/core/styles";
import { useCurrentCustomerContext } from "../../components/current-customer-context";
import JsonEditorDrawer from "./forms/new-form/components/JsonEditorDrawer";
import { useQuery } from "components/graphql/query/use-query";
import { QueryResponseHandler } from "components/graphql";
import { getRoomTemplateQuery } from "./details-page";
import { useAuthDataContext } from "components/auth";
import { getEntityWithoutTypename } from "pages/utils";
import gql from "graphql-tag";
import { buildFilePayload } from "./forms/new-form/utils/buildFilePayload";
import { deriveStreamSourceState } from "./forms/new-form/utils/deriveStreamSourceState";
import { resolveEvuErrorMessage } from "./evu-error-messages";
import Typography from "@material-ui/core/Typography";

const styles = {
  divider_bottom: {
    paddingBottom: 20,
  },
  button_container: {
    display: "flex",
    justifyContent: "center",
    gap: "1rem",
  },
  form_sections_container: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    width: "100%",
    "& > *": {
      width: "100% !important",
      maxWidth: "100% !important",
    },
  },
  page_section: {
    width: "100%",
  },
};

// Human-readable labels for known Yup form field paths.
const FORM_FIELD_LABELS = {
  codename: "Nombre de la plantilla",
  description: "Descripción",
  file: "Configuración del fichero",
  "file.name": "Nombre de la EVU",
  "file.set_dns_names": "Nombres DNS",
  "file.stream_sources": "Fuentes de vídeo/audio",
  "file.virtualcam.config_file": "VirtualCam – Fichero de configuración",
  "file.mixer.score.drawn.cfg_src": "Mixer – URL de configuración",
  "file.mixer.score.titler_url": "Mixer – URL del marcador",
};

const getFormFieldLabel = path => {
  if (FORM_FIELD_LABELS[path]) return FORM_FIELD_LABELS[path];
  if (/stream_sources.*label/i.test(path)) return "Fuentes de vídeo/audio – Etiqueta";
  if (/stream_sources.*url/i.test(path)) return "Fuentes de vídeo/audio – URL";
  if (/stream_sources.*type/i.test(path)) return "Fuentes de vídeo/audio – Tipo";
  if (/stream_sources/i.test(path)) return "Fuentes de vídeo/audio";
  if (/set_dns_names/i.test(path)) return "Nombres DNS";
  if (/virtualcam/i.test(path)) return "VirtualCam";
  if (/mixer/i.test(path)) return "Mixer";
  return path.replace(/^file\./i, "").replace(/\[.*?\]/g, "").replace(/\./g, " › ").replace(/_/g, " ");
};

// Recursively collects { path, message } pairs from a nested RHF errors object.
const collectFormErrors = (node, path = "", depth = 0) => {
  const result = [];
  if (!node || typeof node !== "object" || depth > 6) return result;
  if (node.message && typeof node.message === "string") {
    result.push({ path, message: node.message });
    return result;
  }
  if (Array.isArray(node)) {
    node.forEach((child, i) => {
      if (child && typeof child === "object") {
        result.push(...collectFormErrors(child, path ? `${path}[${i}]` : `[${i}]`, depth + 1));
      }
    });
  } else {
    Object.entries(node).forEach(([key, child]) => {
      if (child && typeof child === "object") {
        result.push(...collectFormErrors(child, path ? `${path}.${key}` : key, depth + 1));
      }
    });
  }
  return result;
};

// Parses EVU tester error messages and calls setError on the matching form fields.
// Patterns handled:
//   "... Field 'A': 'B' is ..."  → file.A.B
//   "... Field 'A' ..."          → file.A
const markEvuErrorsOnFormFields = (evuErrors, setError) => {
  if (!Array.isArray(evuErrors)) return;
  evuErrors.forEach(error => {
    const msg = error.msg ?? (typeof error === "string" ? error : null);
    if (!msg) return;
    const nestedMatch = msg.match(/Field '([^']+)'[^']*'([^']+)'/i);
    if (nestedMatch) {
      setError(`file.${nestedMatch[1]}.${nestedMatch[2]}`, { type: "evu", message: msg });
      return;
    }
    const singleMatch = msg.match(/Field '([^']+)'/i);
    if (singleMatch) {
      setError(`file.${singleMatch[1]}`, { type: "evu", message: msg });
    }
  });
};

const RoomTemplateSharedFormPage = ({
  mode = "create",
  roomTemplateId = null,
  match: {
    params: { contractId },
  },
  intl,
  history,
  classes,
}) => {
  const isEditMode = mode === "edit";
  const { contracts } = useCurrentCustomerContext();
  const { loading, setLoading } = useLoadingContext();
  const { hasAdminPrivileges } = useAuthDataContext();
  const { currentCustomerId } = useCurrentCustomerContext();

  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState(null);
  const [confirmTestSaveOpen, setConfirmTestSaveOpen] = useState(false);
  const [pendingTestSaveValues, setPendingTestSaveValues] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [originalRawJson, setOriginalRawJson] = useState(null);
  const [allStreamSources, setAllStreamSources] = useState([]);
  const [deploymentStreamSources, setDeploymentStreamSources] = useState([]);
  const [selectedStreamSources, setSelectedStreamSources] = useState([]);
  const [selectedRecordings, setSelectedRecordings] = useState([]);
  const [inputMode, setInputMode] = useState(null);
  const [isInitialized, setIsInitialized] = useState(!isEditMode);
  const [initialAudioLinks, setInitialAudioLinks] = useState(null);
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const [expandedAccordions, setExpandedAccordions] = useState({});

  // Note: selectedRecordings are seeded inside the edit-mode init effect below (see reset()).
  // A previous re-seed effect was removed because it could repopulate recordings after the user
  // deliberately cleared them.

  const dummyQuery = useMemo(
    () =>
      gql`
        query DummyQuery {
          __typename
        }
      `,
    [],
  );

  const queryConfig = useMemo(
    () =>
      isEditMode
        ? getRoomTemplateQuery({ roomTemplateId, customerId: currentCustomerId, contractId, hasAdminPrivileges })
        : { query: dummyQuery, skip: true },
    [isEditMode, contractId, currentCustomerId, roomTemplateId, hasAdminPrivileges, dummyQuery],
  );

  const { data, loading: queryLoading, error: queryError } = useQuery(queryConfig);
  const roomTemplateToEdit = isEditMode && data ? data.roomTemplate : null;

  useEffect(
    () => {
      if (isEditMode) {
        const isLoading = queryLoading || !isInitialized || allStreamSources.length === 0;
        setLoading(isLoading);
      }
    },
    [isEditMode, queryLoading, isInitialized, allStreamSources.length, setLoading],
  );

  const {
    handleSubmit: handleFormSubmit,
    watch,
    setValue,
    reset,
    setError: setFormFieldError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema(intl)),
    mode: "onSubmit",
  });

  const errorCount = useMemo(() => {
    return errors ? Object.keys(errors).length : 0;
  }, [errors]);

  useEffect(
    () => {
      if (loading) {
        return;
      }

      if (validationErrors && inputMode === "form") {
        return;
      }

      if (errors && Object.keys(errors).length > 0 && submitAttempt > 0 && !validationErrors) {
        if (errors.file?.stream_sources && Array.isArray(errors.file.stream_sources)) {
          const firstErrorIndex = errors.file.stream_sources.findIndex(
            source => source && Object.keys(source).length > 0,
          );
          if (firstErrorIndex !== -1 && selectedStreamSources[firstErrorIndex] !== undefined) {
            const actualAccordionIndex = selectedStreamSources[firstErrorIndex];
            setExpandedAccordions(prev => ({ ...prev, [actualAccordionIndex]: true }));
          }
        }

        const timeoutId = setTimeout(() => {
          const allErrorFields = document.querySelectorAll(
            ".MuiFormHelperText-root.Mui-error, " +
            '[aria-invalid="true"], ' +
            ".Mui-error input, " +
            ".Mui-error textarea, " +
              ".MuiTypography-colorError",
          );

          let firstErrorField = null;
          for (const errorEl of allErrorFields) {
            const rect = errorEl.getBoundingClientRect();
            const hasContent = errorEl.textContent && errorEl.textContent.trim().length > 0;
            const isVisible = rect.width > 0 && rect.height > 0;

            if (hasContent && isVisible) {
              firstErrorField = errorEl;
              break;
            }
          }

          if (firstErrorField) {
            const accordion = firstErrorField.closest(".MuiAccordion-root");
            const scrollTarget = accordion || firstErrorField;

            const elementPosition = scrollTarget.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - 120;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });

            if (firstErrorField.tagName === "INPUT" || firstErrorField.tagName === "TEXTAREA") {
              firstErrorField.focus();
            }
          } else {
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }
        }, 800);

        return () => clearTimeout(timeoutId);
      }
    },
    [errorCount, submitAttempt, validationErrors, inputMode, selectedStreamSources, loading],
  );

  const values = watch();

  useEffect(
    () => {
      const deploymentId = contracts?.find(contract => contract._id === contractId)?.deployment_id;
      if (deploymentId) {
        setLoading(true);
        getDeploymentStreamSourcesService(
          deploymentId,
          {},
          response => {
            const sources = response.data ?? [];

            const streamSources = sources.map(source => {
              const user = source.users.viewer ?? source.users.admin;
              let url = `${source.public_interface.protocol}://${user.user}:${user.pass}@${
                source.public_interface.dns_entry
              }:${source.public_interface.video_stream_port}/axis-media/media.amp`;

              if (source.brand === "Axis") {
                url = `${url}?audio=0`;
              }

              return {
                description: source.description ?? "",
                cam_id: source.serial ?? "",
                brand: source.brand ?? "",
                model: source.model ?? "",
                url,
                use: source.use ?? "",
                public_interface: {
                  admin_url: source.public_interface?.admin_url ?? "",
                },
              };
            });

            setAllStreamSources(streamSources);
            setDeploymentStreamSources(streamSources);

            if (!isEditMode || (isEditMode && !isInitialized)) {
              if (!isEditMode) {
                setValue("file.stream_sources", []);
              }
            }

            setLoading(false);
          },
          () => {
            setAllStreamSources([]);
            setDeploymentStreamSources([]);
            if (!isEditMode) {
              setValue("file.stream_sources", []);
            }
            setErrorMessage(intl.formatMessage({ id: "app.page.roomTemplates.deploymentSources.error" }));
            setLoading(false);
          },
        );
      }
    },
    [contracts, setValue, isEditMode, contractId],
  );

  useEffect(
    () => {
      const needsInitialization =
        isEditMode &&
        roomTemplateToEdit &&
        deploymentStreamSources.length > 0 &&
        !isInitialized;

      if (needsInitialization) {
        const templateData = getEntityWithoutTypename(roomTemplateToEdit);
        const parsedFile = templateData.file ? JSON.parse(templateData.file) : {};

        const {
          filteredFile,
          allStreamSources: newAllStreamSources,
          selectedIndices,
          audioLinks,
          recordingIndices,
        } = deriveStreamSourceState(parsedFile, deploymentStreamSources);

        setAllStreamSources(newAllStreamSources);
        // Store the original raw JSON so that "Guardar igualmente" can preserve
        // the EVU structure exactly as it was on the server, without any transformation
        // from buildFilePayload (which could alter field order, remove fields, etc.).
        setOriginalRawJson(templateData.file || null);

        reset({
          codename: templateData.codename || "",
          description: templateData.description || "",
          file: filteredFile,
          test: templateData.test || false,
        });

        setInitialAudioLinks(audioLinks);
        setSelectedStreamSources(selectedIndices);
        setSelectedRecordings(recordingIndices);
        setInputMode("form");
        setIsInitialized(true);
        setLoading(false);
      }
    },
    [isEditMode, roomTemplateToEdit, reset, deploymentStreamSources, isInitialized],
  );

  const handleJsonEditorSave = editedJson => {
    const {
      filteredFile,
      allStreamSources: newAllStreamSources,
      selectedIndices,
      audioLinks,
      recordingIndices,
    } = deriveStreamSourceState(editedJson, deploymentStreamSources);

    setAllStreamSources(newAllStreamSources);
    setSelectedStreamSources(selectedIndices);
    setSelectedRecordings(recordingIndices);
    setInitialAudioLinks(audioLinks);
    setExpandedAccordions({});
    setValue("file", filteredFile);
  };

  const onSubmit = async values => {
    setLoading(true);
    setValidationErrors(null);

    let jsonFile;
    if (inputMode !== "form" && inputMode !== "formHidden" && uploadedFile) {
      jsonFile = uploadedFile;
    } else {
      const orderedFileData = buildFilePayload(values);
      const jsonString = JSON.stringify(orderedFileData);
      const jsonBlob = new Blob([jsonString], { type: "application/json" });
      jsonFile = new File([jsonBlob], "template.json", { type: "application/json" });
    }

    const formData = new FormData();
    formData.append("enviroment", getEnvironment());
    formData.append("file", jsonFile);

    checkEvuService(formData)
      .then(r => {
        if (r.status === HTTP_CODE_OK) {
          const formData = new FormData();
          formData.append("codename", values.codename);
          formData.append("description", values.description);
          formData.append("json", jsonFile);
          formData.append("test", values.test);

          const serviceCall = isEditMode
            ? updateRoomTemplateService(contractId, roomTemplateId, formData)
            : createRoomTemplateService(contractId, formData);

          serviceCall
            .then(r => {
              setLoading(false);
              const isSuccess = isEditMode ? r.status === HTTP_CODE_UPDATED : r.status === HTTP_CODE_CREATED;

              if (isSuccess) {
                const targetId = isEditMode ? roomTemplateId : r.data?._id;
                const targetContractId = isEditMode ? contractId : r.data?.contract_id;

                if (targetId && targetContractId) {
                  history.replace(`/roomTemplate/details/${targetId}/contractId/${targetContractId}`);
                }
              }
            })
            .catch(() => {
              setLoading(false);
              const errorKey = isEditMode ? "app.page.roomTemplates.edit.error" : "app.page.roomTemplates.create.error";
              setErrorMessage(intl.formatMessage({ id: errorKey }));
            });
        }
      })
      .catch(e => {
        setLoading(false);
        setErrorMessage(intl.formatMessage({ id: "app.page.roomTemplates.validate.error" }));
        const evuErrors = e.response?.data?.detail;
        if (evuErrors) {
          setValidationErrors(evuErrors);
          markEvuErrorsOnFormFields(evuErrors, setFormFieldError);
        } else {
          setValidationErrors([{ msg: e.message || "Server error occurred" }]);
        }
        // Test templates: warn and ask for confirmation before saving
        if (values.test) {
          setPendingTestSaveValues(values);
          setConfirmTestSaveOpen(true);
        }
      });
  };

  const handleSkipValidation = values => {
    setLoading(true);
    setValidationErrors(null);

    let jsonFile;
    if (isEditMode && originalRawJson) {
      // In edit mode, always preserve the original EVU JSON exactly as it came from
      // the server. buildFilePayload would reorder fields, remove unrecognised ones,
      // and filter stream sources — all of which can silently corrupt a test template
      // that the user is just renaming or re-flagging.
      const jsonBlob = new Blob([originalRawJson], { type: "application/json" });
      jsonFile = new File([jsonBlob], "template.json", { type: "application/json" });
    } else if (inputMode !== "form" && inputMode !== "formHidden" && uploadedFile) {
      jsonFile = uploadedFile;
    } else {
      const orderedFileData = buildFilePayload(values);
      const jsonString = JSON.stringify(orderedFileData);
      const jsonBlob = new Blob([jsonString], { type: "application/json" });
      jsonFile = new File([jsonBlob], "template.json", { type: "application/json" });
    }

    const formData = new FormData();
    formData.append("codename", values.codename);
    formData.append("description", values.description);
    formData.append("json", jsonFile);
    formData.append("test", values.test);

    const serviceCall = isEditMode
      ? updateRoomTemplateService(contractId, roomTemplateId, formData)
      : createRoomTemplateService(contractId, formData);

    serviceCall
      .then(r => {
        setLoading(false);
        const isSuccess = isEditMode ? r.status === HTTP_CODE_UPDATED : r.status === HTTP_CODE_CREATED;

        if (isSuccess) {
          const targetId = isEditMode ? roomTemplateId : r.data?._id;
          const targetContractId = isEditMode ? contractId : r.data?.contract_id;

          if (targetId && targetContractId) {
            history.replace(`/roomTemplate/details/${targetId}/contractId/${targetContractId}`);
          }
        }
      })
      .catch(() => {
        setLoading(false);
        const errorKey = isEditMode ? "app.page.roomTemplates.edit.error" : "app.page.roomTemplates.create.error";
        setErrorMessage(intl.formatMessage({ id: errorKey }));
      });
  };

  const pageTitle = isEditMode ? "app.page.roomTemplate.edit" : "app.page.roomTemplate.new";
  const pageTitleParams = isEditMode && roomTemplateToEdit ? { roomTemplateName: roomTemplateToEdit.codename } : {};

  const handleSubmitWrapper = e => {
    setSubmitAttempt(prev => prev + 1);
    handleFormSubmit(onSubmit)(e);
  };

  const formContent = (
    <form onSubmit={handleSubmitWrapper}>
      <div className={classes.form_sections_container}>
        {errorCount > 0 && submitAttempt > 0 && (() => {
          const seen = new Set();
          const uniqueErrors = collectFormErrors(errors).filter(({ path, message }) => {
            const key = `${getFormFieldLabel(path)}:${message}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return (
            <Alert severity="error" style={{ marginBottom: "0.5rem" }}>
              <Typography variant="body2">
                <strong>
                  <FormattedMessage
                    id="app.form.validation.errors.title"
                    defaultMessage="Hay campos obligatorios sin completar o con errores:"
                  />
                </strong>
              </Typography>
              {uniqueErrors.map(({ path, message }, i) => (
                <Typography key={i} variant="body2">
                  {"• "}
                  <strong>{getFormFieldLabel(path)}</strong>
                  {": "}
                  {message}
                </Typography>
              ))}
            </Alert>
          );
        })()}
        <PageSection className={classes.page_section}>
          <DataGrid>
            <RoomTemplateForm
              setValue={setValue}
              values={values}
              errors={errors}
              handleSkipValidation={handleSkipValidation}
              validationErrors={validationErrors}
              allStreamSources={allStreamSources}
              renderBasicFieldsOnly={true}
              inputMode={inputMode}
              setInputMode={setInputMode}
              skipAutoInitialization={isEditMode}
              onFileUploaded={setUploadedFile}
            />
          </DataGrid>
          {inputMode !== "form" && (
            <>
              <div className={classes.divider_bottom} />
              {validationErrors && (
                <Alert severity="error" style={{ marginBottom: "1rem" }}>
                  <Typography variant="body2">
                    <strong>
                      <FormattedMessage id="roomTemplate.validationErrors" defaultMessage="Validation Errors:" />
                    </strong>
                  </Typography>
                  {Array.isArray(validationErrors) ? (
                    validationErrors.map((error, index) => (
                      <Typography key={`${index}-${error}`} variant="body2">
                        {resolveEvuErrorMessage(error.msg ?? error, intl)}
                      </Typography>
                    ))
                  ) : validationErrors?.msg ? (
                    <Typography variant="body2">{resolveEvuErrorMessage(validationErrors.msg, intl)}</Typography>
                  ) : (
                    <Typography variant="body2">
                      {typeof validationErrors === "string" ? validationErrors : JSON.stringify(validationErrors)}
                    </Typography>
                  )}
                </Alert>
              )}
              <div className={classes.button_container}>
                <Button variant="contained" onClick={() => history.goBack()}>
                  <FormattedMessage id="common.cancel" />
                </Button>
                {!!validationErrors && values.test && (
                  <Button variant="outlined" onClick={() => handleSkipValidation(values)} disabled={isSubmitting}>
                    <FormattedMessage id="app.page.roomTemplates.skip.validation" />
                  </Button>
                )}
                <Button type="submit" disabled={isSubmitting}>
                  <FormattedMessage id="common.accept" />
                </Button>
              </div>
            </>
          )}
        </PageSection>
        {inputMode === "form" && (
          <>
            <PageSection className={classes.page_section}>
              <DataGrid>
                <RoomTemplateForm
                  setValue={setValue}
                  values={values}
                  errors={errors}
                  handleSkipValidation={handleSkipValidation}
                  validationErrors={validationErrors}
                  allStreamSources={allStreamSources}
                  renderSection="fileConfiguration"
                  inputMode={inputMode}
                  setInputMode={setInputMode}
                  skipAutoInitialization={isEditMode}
                />
              </DataGrid>
            </PageSection>
            <PageSection className={classes.page_section}>
              <DataGrid>
                <RoomTemplateForm
                  setValue={setValue}
                  values={values}
                  errors={errors}
                  handleSkipValidation={handleSkipValidation}
                  validationErrors={validationErrors}
                  allStreamSources={allStreamSources}
                  setAllStreamSources={setAllStreamSources}
                  selectedStreamSources={selectedStreamSources}
                  setSelectedStreamSources={setSelectedStreamSources}
                  expandedAccordions={expandedAccordions}
                  setExpandedAccordions={setExpandedAccordions}
                  renderSection="virtualCam"
                  inputMode={inputMode}
                  setInputMode={setInputMode}
                  skipAutoInitialization={isEditMode}
                />
              </DataGrid>
            </PageSection>
            <PageSection className={classes.page_section}>
              <DataGrid>
                <RoomTemplateForm
                  setValue={setValue}
                  values={values}
                  errors={errors}
                  handleSkipValidation={handleSkipValidation}
                  validationErrors={validationErrors}
                  allStreamSources={allStreamSources}
                  setAllStreamSources={setAllStreamSources}
                  selectedStreamSources={selectedStreamSources}
                  setSelectedStreamSources={setSelectedStreamSources}
                  expandedAccordions={expandedAccordions}
                  setExpandedAccordions={setExpandedAccordions}
                  renderSection="streamSources"
                  inputMode={inputMode}
                  setInputMode={setInputMode}
                  skipAutoInitialization={isEditMode}
                  initialAudioLinks={initialAudioLinks}
                />
              </DataGrid>
            </PageSection>
            <PageSection className={classes.page_section}>
              <DataGrid>
                <RoomTemplateForm
                  setValue={setValue}
                  values={values}
                  errors={errors}
                  handleSkipValidation={handleSkipValidation}
                  validationErrors={validationErrors}
                  allStreamSources={allStreamSources}
                  selectedStreamSources={selectedStreamSources}
                  setSelectedStreamSources={setSelectedStreamSources}
                  selectedRecordings={selectedRecordings}
                  setSelectedRecordings={setSelectedRecordings}
                  renderSection="recordings"
                  inputMode={inputMode}
                  setInputMode={setInputMode}
                  skipAutoInitialization={isEditMode}
                />
              </DataGrid>
            </PageSection>
            <PageSection className={classes.page_section}>
              <DataGrid>
                <RoomTemplateForm
                  setValue={setValue}
                  values={values}
                  errors={errors}
                  handleSkipValidation={handleSkipValidation}
                  validationErrors={validationErrors}
                  allStreamSources={allStreamSources}
                  renderSection="mixer"
                  inputMode={inputMode}
                  setInputMode={setInputMode}
                  skipAutoInitialization={isEditMode}
                />
              </DataGrid>
            </PageSection>
            <PageSection className={classes.page_section}>
              <DataGrid>
                <RoomTemplateForm
                  setValue={setValue}
                  values={values}
                  errors={errors}
                  handleSkipValidation={handleSkipValidation}
                  validationErrors={validationErrors}
                  allStreamSources={allStreamSources}
                  renderSection="scoreboard"
                  inputMode={inputMode}
                  setInputMode={setInputMode}
                  skipAutoInitialization={isEditMode}
                />
              </DataGrid>
            </PageSection>
            <PageSection className={classes.page_section}>
              <DataGrid>
                <RoomTemplateForm
                  setValue={setValue}
                  values={values}
                  errors={errors}
                  handleSkipValidation={handleSkipValidation}
                  validationErrors={validationErrors}
                  allStreamSources={allStreamSources}
                  renderSection="advancedSettings"
                  inputMode={inputMode}
                  setInputMode={setInputMode}
                  skipAutoInitialization={isEditMode}
                />
              </DataGrid>
            </PageSection>
            <PageSection className={classes.page_section}>
              {validationErrors && (
                <Alert severity="error" style={{ marginBottom: "1rem" }}>
                  <Typography variant="body2">
                    <strong>
                      <FormattedMessage id="roomTemplate.validationErrors" defaultMessage="Validation Errors:" />
                    </strong>
                  </Typography>
                  {Array.isArray(validationErrors) ? (
                    validationErrors.map((error, index) => (
                      <Typography key={`${index}-${error}`} variant="body2">
                        {resolveEvuErrorMessage(error.msg ?? error, intl)}
                      </Typography>
                    ))
                  ) : validationErrors?.msg ? (
                    <Typography variant="body2">{resolveEvuErrorMessage(validationErrors.msg, intl)}</Typography>
                  ) : (
                    <Typography variant="body2">
                      {typeof validationErrors === "string" ? validationErrors : JSON.stringify(validationErrors)}
                    </Typography>
                  )}
                </Alert>
              )}
              <div className={classes.button_container}>
                <Button variant="contained" onClick={() => history.goBack()}>
                  <FormattedMessage id="common.cancel" />
                </Button>
                {!!validationErrors && values.test && (
                  <Button variant="outlined" onClick={() => handleSkipValidation(values)} disabled={isSubmitting}>
                    <FormattedMessage id="app.page.roomTemplates.skip.validation" />
                  </Button>
                )}
                <Button type="submit" disabled={isSubmitting}>
                  <FormattedMessage id="common.accept" />
                </Button>
              </div>
            </PageSection>
          </>
        )}
      </div>
    </form>
  );

  const confirmSaveDialog = (
    <Dialog open={confirmTestSaveOpen} onClose={() => setConfirmTestSaveOpen(false)}>
      <DialogTitle>
        <FormattedMessage id="app.page.roomTemplates.testSaveConfirm.title" defaultMessage="Plantilla con errores EVU" />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" style={{ marginBottom: "1rem" }}>
          <FormattedMessage
            id="app.page.roomTemplates.testSaveConfirm.body"
            defaultMessage="La plantilla tiene errores de validación EVU. Al ser una plantilla de prueba puedes guardarla igualmente, pero ten en cuenta que podría no funcionar correctamente."
          />
        </Typography>
        {validationErrors && (
          <Alert severity="warning" style={{ marginTop: "0.5rem" }}>
            {Array.isArray(validationErrors) ? (
              validationErrors.map((e, i) => (
                <Typography key={i} variant="body2">{resolveEvuErrorMessage(e.msg ?? e, intl)}</Typography>
              ))
            ) : validationErrors?.msg ? (
              <Typography variant="body2">{resolveEvuErrorMessage(validationErrors.msg, intl)}</Typography>
            ) : null}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => {
            setConfirmTestSaveOpen(false);
            setPendingTestSaveValues(null);
          }}
        >
          <FormattedMessage id="common.cancel" />
        </Button>
        <Button
          onClick={() => {
            setConfirmTestSaveOpen(false);
            if (pendingTestSaveValues) {
              handleSkipValidation(pendingTestSaveValues);
              setPendingTestSaveValues(null);
            }
          }}
        >
          <FormattedMessage id="app.page.roomTemplates.testSaveConfirm.confirm" defaultMessage="Guardar igualmente" />
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (isEditMode) {
    return (
      <>
        <Dashboard>
          <QueryResponseHandler error={queryError} loading={queryLoading} elements={roomTemplateToEdit}>
            <PageHeaderBar title={pageTitle} titleParams={pageTitleParams} />
            {formContent}
            <Snackbar
              open={!!errorMessage}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              autoHideDuration={6000}
              onClose={() => setErrorMessage("")}
            >
              <Alert onClose={() => setErrorMessage("")} severity="error">
                {errorMessage}
              </Alert>
            </Snackbar>
            {(inputMode === "form" || inputMode === "formHidden") && (
              <JsonEditorDrawer values={values} onSave={handleJsonEditorSave} />
            )}
          </QueryResponseHandler>
        </Dashboard>
        {confirmSaveDialog}
      </>
    );
  }

  return (
    <>
    <Dashboard>
      <PageHeaderBar title={pageTitle} titleParams={pageTitleParams} />
      {formContent}
      <Snackbar
        open={!!errorMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={6000}
        onClose={() => setErrorMessage("")}
      >
        <Alert onClose={() => setErrorMessage("")} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
      {(inputMode === "form" || inputMode === "formHidden") && (
        <JsonEditorDrawer values={values} onSave={handleJsonEditorSave} />
      )}
    </Dashboard>
    {confirmSaveDialog}
    </>
  );
};

export default withStyles(styles)(RoomTemplateSharedFormPage);
